import { t } from "@/i18n-js/instance";
import { useGetCouponApplication } from "@circle-react/components/Paywalls/Checkout/hooks/useGetCouponApplication";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import {
  isOneTimePrice,
  isSubscriptionPrice,
} from "@circle-react/helpers/paywallPriceHelpers";
import { formatPaywallCurrencyAmount } from "@circle-react/helpers/paywalls/currencies";
import { useIsCouponStrikethroughEnabled } from "@circle-react/hooks/useIsCouponStrikethroughEnabled";
import type { CouponApplication, Paywall } from "@circle-react/types/Paywall";
import type {
  PaywallPrice,
  PaywallPriceScenario,
} from "@circle-react/types/PaywallPrice";
import { PaywallCustomization } from "../../PaywallCustomization";
import { CouponTag } from "./CouponTag";
import { Price } from "./Price";
import { defaultFormatPaywallCurrency } from "./constants";
import type { PriceScenariosProps } from "./types";

const scenariosResolver: Record<
  PaywallPriceScenario,
  Array<(props: PriceScenariosProps) => React.ReactNode>
> = {
  value: [Price],
  trial_then_value: [Price.Badge, Price.ValueWithArrow],
  trial_then_upfront_then_value: [
    Price.Badge,
    Price.IntroFeeWithArrow,
    Price.ValueWithArrow,
  ],
  upfront_then_value: [Price.IntroFee, Price.ValueWithArrow],
  upfront_then_trial_then_value: [
    Price.IntroFee,
    Price.BadgeWithArrow,
    Price.ValueWithArrow,
  ],
};

const titleLocales = {
  subscription: (intervalType: string) => [
    "settings.paywall_prices.subscription.title",
    intervalType,
  ],
  onetime: () => ["settings.paywall_prices.onetime.type_qualifier"],
  installments: () => [],
};

const getSubtitle = (
  price: PaywallPrice,
  paywall: Paywall,
  couponApplication: ReturnType<typeof useGetCouponApplication>,
) => {
  if (isSubscriptionPrice(price) || isOneTimePrice(price)) {
    return {
      subtitle: t(titleLocales[price.price_type](price.interval_type)),
    };
  }

  const totalDiscount = couponApplication.applications.reduce(
    (accumulator: number, x: CouponApplication) =>
      x.discount_amount + accumulator,
    0,
  );

  const prefix =
    totalDiscount > 0
      ? formatPaywallCurrencyAmount(
          paywall.currency,
          price.amount,
          defaultFormatPaywallCurrency,
        )
      : undefined;

  return {
    prefix,
    subtitle: t(
      [
        "paywall_checkout.price_value.title",
        price.price_type,
        price.interval_type,
      ],
      {
        total_amount: formatPaywallCurrencyAmount(
          paywall.currency,
          Math.max(0, price.amount - totalDiscount),
          defaultFormatPaywallCurrency,
        ),
      },
    ),
  };
};

export const PriceDescription = ({ price, paywall }: PriceScenariosProps) => {
  const { couponPreviewMutation } = usePaywallCheckoutContext();
  const couponApplications = useGetCouponApplication(
    price.id,
    couponPreviewMutation,
  );
  const components = scenariosResolver[price.scenario];
  const isCouponStrikethroughEnabled = useIsCouponStrikethroughEnabled();

  return (
    <div className="flex flex-row justify-between">
      <div className="flex flex-col gap-y-1">
        <div key={price.id} className="leading-6">
          {components.map((component, index) =>
            component({
              price,
              paywall,
              couponApplication: isCouponStrikethroughEnabled
                ? couponApplications?.applications?.[index]
                : undefined,
            }),
          )}
        </div>
        <PaywallCustomization.PriceCallout price={price}>
          <Price.Subtitle
            {...getSubtitle(price, paywall, couponApplications)}
          />
        </PaywallCustomization.PriceCallout>
      </div>
      <CouponTag paywallPriceId={price.id} />
    </div>
  );
};
